import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/bi/AppShell";
import { EmptyState } from "@/components/bi/EmptyState";
import { Loading } from "@/components/bi/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, LayoutDashboard, MoreVertical, Copy, Trash2, Share2, Clock, Tv as TvIcon, Wand2, Eye, Users, Link as LinkIcon, Rocket, EyeOff, Archive, ArchiveRestore, FolderKanban, Pencil, ArrowRightLeft, Home, Tag, Upload, Building2 } from "lucide-react";
import { PortabilityDialog } from "@/components/bi/PortabilityDialog";
import { DashboardCategoryField } from "@/components/bi/AccessGrants";
import { StatusBadge, STATUS_META, A11yScoreBadge } from "@/components/bi/StatusBadge";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { OnboardingWizard } from "@/components/bi/OnboardingWizard";
import { DashboardWizardDialog } from "@/components/bi/DashboardWizardDialog";
import { useAuth } from "@/context/AuthContext";
import { dedupGet } from "@/lib/dedupGet";
import { ManagedCopyDialog } from "@/components/bi/ManagedCopyDialog";
import { isCrossTenant } from "@/lib/roles";

export default function Dashboards() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { bootstrap, user } = useAuth();
  const staff = isCrossTenant(user?.role);
  const [items, setItems] = useState(null);
  const [provDash, setProvDash] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [tvOpen, setTvOpen] = useState(false);
  const [shareStats, setShareStats] = useState({}); // {dashboard_id: stats}
  const [sites, setSites] = useState(bootstrap?.sites || []);
  const [siteFilter, setSiteFilter] = useState(searchParams.get("site") || "all");
  const [sitesOpen, setSitesOpen] = useState(false);
  const [catDlg, setCatDlg] = useState(null); // {id, name, category}
  const [portDash, setPortDash] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    try { const { data } = await dedupGet("/dashboards"); setItems(data); }
    catch (e) { toast.error("Failed to load dashboards"); setItems([]); }
  };
  const loadSites = () => api.get("/sites").then(({ data }) => setSites(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  // Prefer sites already delivered by /auth/bootstrap. Fall back to /sites only
  // if bootstrap didn't ship any (bootstrap failed / feature disabled) — cuts
  // the post-login /sites fetch storm from 4x to 0x on the happy path.
  useEffect(() => {
    if (bootstrap?.sites && bootstrap.sites.length > 0) setSites(bootstrap.sites);
    else if (bootstrap && (!bootstrap.sites || bootstrap.sites.length === 0)) loadSites();
  }, [bootstrap]);
  useEffect(() => {
    if (siteFilter !== "all" && sites.length && !sites.some((s) => s.id === siteFilter)) setSiteFilter("all");
  }, [sites, siteFilter]);

  const moveToSite = async (id, siteId) => {
    try { await api.post(`/sites/move-dashboard/${id}`, { site_id: siteId }); toast.success("Moved"); load(); loadSites(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to move"); }
  };

  // Batch-load share stats once dashboards are known; only for those actually shared/embedded.
  useEffect(() => {
    if (!items || items.length === 0) return;
    const ids = items.filter((d) => d.is_public || d.embed_enabled).map((d) => d.id);
    if (ids.length === 0) return;
    api.get("/dashboards/share-stats/batch", { params: { ids: ids.join(",") } })
      .then(({ data }) => setShareStats(data || {}))
      .catch(() => {});
  }, [items]);

  const create = async () => {
    if (!name.trim()) { toast.error("Enter a name"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/dashboards", { name: name.trim(), description: desc });
      setOpen(false); setName(""); setDesc("");
      nav(`/builder/${data.id}`);
    } catch (e) { toast.error("Failed to create"); } finally { setCreating(false); }
  };

  const duplicate = async (id) => { try { await api.post(`/dashboards/${id}/duplicate`); toast.success("Duplicated"); load(); } catch { toast.error("Failed"); } };
  const del = async (id) => { try { await api.delete(`/dashboards/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const publish = async (id) => { try { await api.post(`/dashboards/${id}/publish`, { note: "" }); toast.success("Published"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const unpublish = async (id) => { try { await api.post(`/dashboards/${id}/unpublish`); toast.success("Unpublished"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const setStatus = async (id, status) => { try { await api.post(`/dashboards/${id}/status`, { status }); toast.success(`Moved to ${STATUS_META[status]?.label || status}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const [statusFilter, setStatusFilter] = useState("all");
  const can = (d, action) => (d.my_actions ? d.my_actions.includes(action) : d.my_role !== "viewer");

  const [wizardOpen, setWizardOpen] = useState(false);

  const topBtn = (
    <div className="flex items-center gap-2">
      <Button data-testid="manage-sites-btn" variant="outline" onClick={() => setSitesOpen(true)} className="bg-[color:var(--surface)]"><FolderKanban className="w-4 h-4 mr-1" />Sites</Button>
      <Button data-testid="import-dashboard-btn" variant="outline" onClick={() => setImportOpen(true)} className="bg-[color:var(--surface)]"><Upload className="w-4 h-4 mr-1" />Import</Button>
      <Button data-testid="tv-mode-btn" variant="outline" onClick={() => setTvOpen(true)} className="bg-[color:var(--surface)]"><TvIcon className="w-4 h-4 mr-1" />TV mode</Button>
      <Button data-testid="ai-wizard-btn" variant="outline" onClick={() => setWizardOpen(true)} className="border-[color:var(--brand-mint)] text-[color:var(--brand-mint)] hover:bg-[color:rgba(0,194,168,0.10)]"><Wand2 className="w-4 h-4 mr-1" />AI Wizard</Button>
      <Button data-testid="new-dashboard-btn" onClick={() => setOpen(true)} className="bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white"><Plus className="w-4 h-4 mr-1" />New Dashboard</Button>
    </div>
  );

  return (
    <AppShell title="Dashboards" actions={topBtn}>
      <DashboardWizardDialog open={wizardOpen} onOpenChange={setWizardOpen} />
      <Dialog open={!!catDlg} onOpenChange={(o) => { if (!o) setCatDlg(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Tag className="w-5 h-5 text-[color:var(--brand-ocean)]" />Category — {catDlg?.name}</DialogTitle></DialogHeader>
          {catDlg && <DashboardCategoryField compact dashboardId={catDlg.id} category={catDlg.category} onChange={() => { setCatDlg(null); load(); }} />}
        </DialogContent>
      </Dialog>
      <TvModeDialog open={tvOpen} onOpenChange={setTvOpen} dashboards={items || []} />
      <SitesDialog open={sitesOpen} onOpenChange={setSitesOpen} sites={sites} onChanged={() => { loadSites(); load(); }} />
      <PortabilityDialog open={!!portDash} onOpenChange={(o) => { if (!o) setPortDash(null); }} mode="replicate"
        dashboard={portDash} onDone={() => { setPortDash(null); load(); loadSites(); }} />
      <PortabilityDialog open={importOpen} onOpenChange={setImportOpen} mode="import"
        onDone={() => { setImportOpen(false); load(); loadSites(); }} />
      <ManagedCopyDialog open={!!provDash} onOpenChange={(o) => { if (!o) setProvDash(null); }} kind="dashboard"
        item={provDash} onDone={() => { setProvDash(null); load(); }} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Create dashboard</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Name</Label><Input data-testid="dashboard-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Q4 Sales Overview" className="mt-1.5" /></div>
            <div><Label>Description</Label><Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Optional" className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button data-testid="dashboard-create-confirm" onClick={create} disabled={creating} className="bg-[color:var(--brand-ocean)] text-white">Create & open builder</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="px-5 md:px-8 py-6">
        <OnboardingWizard onCreateDashboard={() => setOpen(true)} />
        {items === null ? <Loading label="Loading dashboards" /> :
          items.length === 0 ? (
            <EmptyState testid="dashboards-empty" icon={LayoutDashboard} title="No dashboards yet" description="Create your first dashboard and start dragging widgets onto the canvas." action={<Button data-testid="new-dashboard-empty-btn" onClick={() => setOpen(true)} className="bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white"><Plus className="w-4 h-4 mr-1" />New Dashboard</Button>} />
          ) : (
            <>
            {sites.length > 1 && (
              <div data-testid="site-filter-chips" className="flex flex-wrap items-center gap-1.5 mb-2">
                <button data-testid="site-chip-all" onClick={() => setSiteFilter("all")}
                  className={cn("text-[11px] px-2.5 py-1 rounded-full border transition-colors",
                    siteFilter === "all" ? "bg-[color:var(--brand-mint)] text-white border-transparent" : "border-[color:var(--glass-border)] text-[color:var(--ink-3)] hover:border-[color:var(--brand-mint)]")}>
                  All sites · {items.length}
                </button>
                {sites.map((s) => (
                  <button key={s.id} data-testid={`site-chip-${s.id}`} onClick={() => setSiteFilter(s.id)}
                    className={cn("text-[11px] px-2.5 py-1 rounded-full border transition-colors",
                      siteFilter === s.id ? "bg-[color:var(--brand-mint)] text-white border-transparent" : "border-[color:var(--glass-border)] text-[color:var(--ink-3)] hover:border-[color:var(--brand-mint)]")}>
                    {s.name} · {items.filter((d) => d.site_id === s.id).length}
                  </button>
                ))}
                {siteFilter !== "all" && (
                  <button data-testid="site-open-home-inline" onClick={() => nav(`/sites/${siteFilter}`)}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-[color:var(--brand-ocean)] text-[color:var(--brand-ocean)] hover:bg-[color:rgba(43,78,255,0.08)] flex items-center gap-1">
                    <Home className="w-3 h-3" />Open site home
                  </button>
                )}
              </div>
            )}
            <div data-testid="status-filter-chips" className="flex flex-wrap gap-1.5 mb-4">
              {["all", "development", "published", "hidden", "archived"].map((s) => {
                const count = s === "all" ? items.length : items.filter((d) => (d.status || "development") === s).length;
                if (s !== "all" && count === 0) return null;
                return (
                  <button key={s} data-testid={`status-chip-${s}`} onClick={() => setStatusFilter(s)}
                    className={cn("text-[11px] px-2.5 py-1 rounded-full border transition-colors capitalize",
                      statusFilter === s ? "bg-[color:var(--brand-ocean)] text-white border-transparent" : "border-[color:var(--glass-border)] text-[color:var(--ink-3)] hover:border-[color:var(--brand-ocean)]")}>
                    {s} · {count}
                  </button>
                );
              })}
            </div>
            {(() => {
              const filtered = items.filter((d) => (statusFilter === "all" || (d.status || "development") === statusFilter) && (siteFilter === "all" || d.site_id === siteFilter));
              if (filtered.length === 0) {
                const siteName = siteFilter === "all" ? null : (sites.find((s) => s.id === siteFilter)?.name);
                return (
                  <div data-testid="dashboards-filter-empty" className="glass-card border border-dashed border-[color:var(--glass-border)] p-8 text-center">
                    <LayoutDashboard className="w-8 h-8 mx-auto text-[color:var(--ink-3)] opacity-60" />
                    <div className="text-sm text-[color:var(--ink-2)] mt-3 font-medium">No dashboards match the current filter</div>
                    <div className="text-xs text-[color:var(--ink-3)] mt-1">
                      {siteName ? `${siteName} has no ${statusFilter === "all" ? "dashboards" : statusFilter + " dashboards"} yet` : `No ${statusFilter} dashboards`}
                    </div>
                    <div className="mt-4 flex items-center justify-center gap-2">
                      {siteFilter !== "all" && <Button data-testid="dashboards-filter-clear-site" variant="outline" size="sm" onClick={() => setSiteFilter("all")}>Show all sites</Button>}
                      {statusFilter !== "all" && <Button data-testid="dashboards-filter-clear-status" variant="outline" size="sm" onClick={() => setStatusFilter("all")}>Clear status filter</Button>}
                      <Button data-testid="dashboards-filter-empty-new" onClick={() => setOpen(true)} className="bg-[color:var(--brand-ocean)] text-white" size="sm"><Plus className="w-4 h-4 mr-1" />New Dashboard</Button>
                    </div>
                  </div>
                );
              }
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filtered.map((d) => (
                <div key={d.id} data-testid="dashboard-card" className="glass-card p-5 cursor-pointer hover:shadow-[0_22px_70px_rgba(15,23,42,0.14)] transition-shadow group" onClick={() => nav(`/builder/${d.id}`)}>
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl hero-gradient flex items-center justify-center border border-[color:var(--glass-border)]"><LayoutDashboard className="w-5 h-5 text-[color:var(--brand-ocean)]" /></div>
                    <div className="flex items-center gap-1">
                      <A11yScoreBadge a11y={d.a11y} onClick={() => nav(`/builder/${d.id}?a11y=1`)} />
                      <StatusBadge status={d.status} />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}><button data-testid={`card-menu-${d.id}`} className="opacity-0 group-hover:opacity-100 transition-opacity text-[color:var(--ink-3)] p-1"><MoreVertical className="w-4 h-4" /></button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem onClick={() => duplicate(d.id)}><Copy className="w-4 h-4 mr-2" />Duplicate</DropdownMenuItem>
                          {can(d, "edit") && sites.filter((s) => s.id !== d.site_id).slice(0, 8).map((s) => (
                            <DropdownMenuItem key={s.id} data-testid={`card-move-site-${s.id}`} onClick={() => moveToSite(d.id, s.id)}><ArrowRightLeft className="w-4 h-4 mr-2" />Move to {s.name}</DropdownMenuItem>
                          ))}
                          {can(d, "publish") && (d.status || "development") !== "published" && (d.status || "development") !== "archived" &&
                            <DropdownMenuItem data-testid="card-publish" onClick={() => publish(d.id)}><Rocket className="w-4 h-4 mr-2" />Publish</DropdownMenuItem>}
                          {can(d, "publish") && d.status === "published" &&
                            <DropdownMenuItem data-testid="card-unpublish" onClick={() => unpublish(d.id)}><EyeOff className="w-4 h-4 mr-2" />Unpublish</DropdownMenuItem>}
                          {can(d, "manage_permissions") && d.status !== "hidden" && d.status !== "archived" &&
                            <DropdownMenuItem data-testid="card-hide" onClick={() => setStatus(d.id, "hidden")}><EyeOff className="w-4 h-4 mr-2" />Hide</DropdownMenuItem>}
                          {can(d, "manage_permissions") && d.status === "hidden" &&
                            <DropdownMenuItem data-testid="card-unhide" onClick={() => setStatus(d.id, "development")}><Eye className="w-4 h-4 mr-2" />Unhide</DropdownMenuItem>}
                          {can(d, "manage_permissions") && d.status !== "archived" &&
                            <DropdownMenuItem data-testid="card-archive" onClick={() => setStatus(d.id, "archived")}><Archive className="w-4 h-4 mr-2" />Archive</DropdownMenuItem>}
                          {can(d, "publish") && d.status === "archived" &&
                            <DropdownMenuItem data-testid="card-restore" onClick={() => setStatus(d.id, "development")}><ArchiveRestore className="w-4 h-4 mr-2" />Restore</DropdownMenuItem>}
                          {can(d, "export") &&
                            <DropdownMenuItem data-testid={`card-port-${d.id}`} onClick={() => setPortDash(d)}><ArrowRightLeft className="w-4 h-4 mr-2" />Copy / move / download…</DropdownMenuItem>}
                          {staff &&
                            <DropdownMenuItem data-testid={`card-provision-${d.id}`} onClick={() => setProvDash(d)}><Building2 className="w-4 h-4 mr-2" />Provision into a tenant…</DropdownMenuItem>}
                          {can(d, "manage_permissions") &&
                            <DropdownMenuItem data-testid="card-set-category" onClick={() => setCatDlg({ id: d.id, name: d.name, category: d.category || "" })}><Tag className="w-4 h-4 mr-2" />{d.category ? "Change category…" : "Set category…"}</DropdownMenuItem>}
                          {can(d, "delete") &&
                            <DropdownMenuItem onClick={() => del(d.id)} className="text-[color:var(--danger)]"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <h3 className="font-display text-lg text-[color:var(--ink)] mt-4 truncate">{d.name}</h3>
                  <p className="text-sm text-[color:var(--ink-3)] mt-1 line-clamp-2 min-h-[40px]">{d.description || "No description"}</p>
                  <div className="flex items-center justify-between mt-4 text-xs text-[color:var(--ink-3)]">
                    <span className="flex items-center gap-1"><LayoutDashboard className="w-3.5 h-3.5" />{(d.widgets || []).length} widgets</span>
                    <span className="flex items-center gap-2">
                      {d.category && <span data-testid="card-category-chip" className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[color:var(--surface-2)] text-[color:var(--ink-2)]"><Tag className="w-3 h-3" />{d.category}</span>}
                      {d.is_public && <span className="flex items-center gap-1 text-[color:var(--brand-mint)]"><Share2 className="w-3.5 h-3.5" />Shared</span>}
                    </span>
                  </div>
                  <ShareAnalyticsBlock stats={shareStats[d.id]} shared={!!(d.is_public || d.embed_enabled)} />
                </div>
                  ))}
                </div>
              );
            })()}
            </>
          )}
      </div>
    </AppShell>
  );
}

function SitesDialog({ open, onOpenChange, sites, onChanged }) {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!name.trim()) return toast.error("Enter a site name");
    setBusy(true);
    try { await api.post("/sites", { name: name.trim() }); setName(""); toast.success("Site created"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const rename = async (s) => {
    const next = window.prompt("Rename site", s.name);
    if (!next || next === s.name) return;
    try { await api.patch(`/sites/${s.id}`, { name: next }); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete site "${s.name}"? Its dashboards move to the default site.`)) return;
    try { await api.delete(`/sites/${s.id}`); toast.success("Site deleted"); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openHome = (s) => { onOpenChange(false); nav(`/sites/${s.id}`); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><FolderKanban className="w-5 h-5 text-[color:var(--brand-ocean)]" />Sites / workspaces</DialogTitle></DialogHeader>
        <p className="text-xs text-[color:var(--ink-3)]">Group dashboards into sites. Each site has its own home landing view with featured dashboards.</p>
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {sites.map((s) => (
            <div key={s.id} data-testid={`site-row-${s.id}`} className="flex items-center justify-between text-sm bg-[color:var(--surface-2)] rounded-lg px-3 py-2">
              <span className="truncate">{s.name}
                {s.is_default && <span className="text-[9px] uppercase text-[color:var(--brand-ocean)] font-semibold ml-1.5">default</span>}
                <span className="text-[10px] text-[color:var(--ink-3)] ml-1.5">{s.dashboard_count} dashboards</span>
              </span>
              <span className="flex items-center gap-1 shrink-0">
                <button data-testid={`site-home-${s.id}`} onClick={() => openHome(s)} title="Open site home"
                  className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--brand-mint)]"><Home className="w-3.5 h-3.5" /></button>
                <button data-testid={`site-rename-${s.id}`} onClick={() => rename(s)} title="Rename"
                  className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]"><Pencil className="w-3.5 h-3.5" /></button>
                {!s.is_default && <button data-testid={`site-delete-${s.id}`} onClick={() => del(s)} title="Delete"
                  className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><Trash2 className="w-3.5 h-3.5" /></button>}
              </span>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input data-testid="site-new-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="New site name" />
          <Button data-testid="site-create-btn" onClick={create} disabled={busy} className="bg-[color:var(--brand-ocean)] text-white shrink-0"><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TvModeDialog({ open, onOpenChange, dashboards }) {
  const nav = useNavigate();
  const [selected, setSelected] = useState([]);
  const [interval, setIntervalSec] = useState(30);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const start = () => {
    if (!selected.length) { toast.error("Pick at least one dashboard"); return; }
    onOpenChange(false);
    nav(`/tv?ids=${selected.join(",")}&interval=${Math.max(10, Number(interval) || 30)}`);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><TvIcon className="w-5 h-5 text-[color:var(--brand-ocean)]" />TV / kiosk mode</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label className="text-xs">Dashboards to rotate</Label>
            <div className="mt-1.5 max-h-52 overflow-y-auto space-y-1 rounded-lg border border-[color:var(--glass-border)] p-2">
              {dashboards.map((d) => (
                <label key={d.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[color:var(--surface-2)] cursor-pointer">
                  <Checkbox data-testid="tv-dashboard-check" checked={selected.includes(d.id)} onCheckedChange={() => toggle(d.id)} />
                  <span className="text-sm text-[color:var(--ink-2)] truncate">{d.name}</span>
                </label>
              ))}
              {dashboards.length === 0 && <div className="text-xs text-[color:var(--ink-3)] text-center py-4">No dashboards yet</div>}
            </div>
          </div>
          <div><Label className="text-xs">Rotate / refresh every (seconds)</Label>
            <Input data-testid="tv-interval" type="number" min={10} className="mt-1.5 w-[120px]" value={interval} onChange={(e) => setIntervalSec(e.target.value)} />
          </div>
          <p className="text-[11px] text-[color:var(--ink-3)]">Fullscreen auto-rotating display — press Esc to exit. Single dashboard auto-refreshes on the interval.</p>
        </div>
        <DialogFooter><Button data-testid="tv-start-btn" onClick={start} className="bg-[color:var(--brand-ocean)] text-white"><TvIcon className="w-4 h-4 mr-1" />Start TV mode</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function relTime(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.round(d / 7);
  if (w < 5) return `${w}w ago`;
  return new Date(iso).toLocaleDateString();
}

function Sparkline({ points }) {
  const vals = points.map((p) => p.views || 0);
  const max = Math.max(...vals, 1);
  const w = 120, h = 26, step = w / (vals.length - 1 || 1);
  const y = (v) => h - 3 - (v / max) * (h - 8);
  const path = vals.map((v, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const area = `${path} L${w},${h} L0,${h} Z`;
  return (
    <svg data-testid="dashboard-card-sparkline" width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0" aria-label="Views over the last 7 days">
      <path d={area} fill="var(--brand-ocean)" opacity="0.12" />
      <path d={path} fill="none" stroke="var(--brand-ocean)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={(vals.length - 1) * step} cy={y(vals[vals.length - 1])} r="2" fill="var(--brand-ocean)" />
    </svg>
  );
}

function ShareAnalyticsBlock({ stats, shared }) {
  if (!shared) return null;
  const s = stats || {};
  return (
    <div data-testid="dashboard-card-analytics"
      className="mt-3 pt-3 border-t border-[color:var(--glass-border)] grid grid-cols-3 gap-2 text-[11px] text-[color:var(--ink-3)]">
      <div className="flex flex-col">
        <span className="flex items-center gap-1 text-[color:var(--ink-2)] font-semibold text-xs">
          <Eye className="w-3 h-3" />{stats ? (s.total_views ?? 0) : "…"}
        </span>
        <span>views</span>
      </div>
      <div className="flex flex-col">
        <span className="flex items-center gap-1 text-[color:var(--ink-2)] font-semibold text-xs">
          <Users className="w-3 h-3" />{stats ? (s.unique_visitors ?? 0) : "…"}
        </span>
        <span>visitors</span>
      </div>
      <div className="flex flex-col min-w-0">
        <span className="flex items-center gap-1 text-[color:var(--ink-2)] font-semibold text-xs whitespace-nowrap">
          <Clock className="w-3 h-3" />{stats ? relTime(s.last_viewed) : "…"}
        </span>
        <span>last viewed</span>
      </div>
      {stats && s.daily_7d && (s.views_7d || 0) > 0 && (
        <div className="col-span-3 flex items-center justify-between gap-2 mt-1">
          <span className="text-[10px] uppercase tracking-wide">7-day trend</span>
          <Sparkline points={s.daily_7d} />
        </div>
      )}
      {stats && s.top_referrer && (
        <div data-testid="dashboard-card-top-referrer" className="col-span-3 flex items-center gap-1 truncate mt-0.5">
          <LinkIcon className="w-3 h-3 text-[color:var(--brand-mint)]" />
          <span className="truncate">
            Top referrer: <span className="text-[color:var(--ink-2)] font-medium">{s.top_referrer.host}</span>
            <span className="opacity-60"> · {s.top_referrer.views}</span>
          </span>
        </div>
      )}
    </div>
  );
}
